import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, FileText, Shield, Award, Building, CheckCircle } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export const GovernmentPage = () => {
  return (
    <main className="bg-black pt-20" data-testid="government-page">
      {/* Hero Section */}
      <section className="py-24 bg-zinc-950 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="badge bg-orange-500/10 border border-orange-500/50 text-orange-500 px-4 py-2 mb-6 inline-block">
              Government Contracting
            </span>
            <h1 className="text-5xl sm:text-6xl md:text-7xl uppercase tracking-wider font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Government & Compliance
            </h1>
            <p className="text-zinc-300 text-lg leading-relaxed">
              23rd Industries is fully registered and compliant for government contracting. 
              We're ready to support federal, state, and local government cleaning requirements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Credentials Dashboard */}
      <section className="py-16 bg-black" data-testid="credentials-dashboard">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl uppercase tracking-wide font-bold text-white mb-12" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Government Credentials
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* UEI */}
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="bg-zinc-950 border border-zinc-800 p-8 hover:border-orange-500 transition-colors"
              data-testid="credential-uei"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-orange-600 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-black" />
                </div>
                <div>
                  <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">Unique Entity ID</span>
                  <h3 className="text-xl uppercase tracking-wide font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>UEI</h3>
                </div>
              </div>
              <div className="bg-black border border-zinc-800 p-4">
                <span className="font-mono text-orange-500 text-lg tracking-wider">HWVZSJ4EWJ27</span>
              </div>
            </motion.div>

            {/* CAGE Code */}
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-zinc-950 border border-zinc-800 p-8 hover:border-orange-500 transition-colors"
              data-testid="credential-cage"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-orange-600 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-black" />
                </div>
                <div>
                  <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">Commercial & Govt Entity</span>
                  <h3 className="text-xl uppercase tracking-wide font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>CAGE Code</h3>
                </div>
              </div>
              <div className="bg-black border border-zinc-800 p-4">
                <span className="font-mono text-orange-500 text-lg tracking-wider">1AQ57</span>
              </div>
            </motion.div>

            {/* Business Type */}
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-zinc-950 border border-zinc-800 p-8 hover:border-orange-500 transition-colors"
              data-testid="credential-business"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-orange-600 flex items-center justify-center">
                  <Building className="w-6 h-6 text-black" />
                </div>
                <div>
                  <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">Entity Type</span>
                  <h3 className="text-xl uppercase tracking-wide font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Business Type</h3>
                </div>
              </div>
              <div className="bg-black border border-zinc-800 p-4">
                <span className="font-mono text-orange-500 text-sm">LLC | Minority-Owned | Woman-Owned</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* NAICS Codes */}
      <section className="py-16 bg-zinc-950 border-t border-zinc-800" data-testid="naics-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl uppercase tracking-wide font-bold text-white mb-12" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            NAICS Codes
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { code: '561790', title: 'Other Services to Buildings', desc: 'Building cleaning and maintenance services' },
              { code: '238990', title: 'All Other Specialty Trade', desc: 'Specialty trade contractors' },
              { code: '811310', title: 'Commercial Machinery Repair', desc: 'Machinery maintenance and repair' },
            ].map((naics, i) => (
              <motion.div
                key={naics.code}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-black border border-zinc-800 p-6 hover:border-orange-500 transition-colors"
                data-testid={`naics-${naics.code}`}
              >
                <span className="font-mono text-orange-500 text-2xl tracking-wider">{naics.code}</span>
                <h3 className="text-lg uppercase tracking-wide font-bold text-white mt-4 mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {naics.title}
                </h3>
                <p className="text-zinc-400 text-sm">{naics.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 bg-black border-t border-zinc-800" data-testid="gov-certifications">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl uppercase tracking-wide font-bold text-white mb-12" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Certifications & Insurance
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Award, title: 'Minority-Owned', status: 'Certified' },
              { icon: Award, title: 'Woman-Owned', status: 'Certified' },
              { icon: Shield, title: 'General Liability', status: '$1M+ Coverage' },
              { icon: CheckCircle, title: 'SAM Registered', status: 'Active' },
            ].map((cert, i) => (
              <div key={i} className="bg-zinc-950 border border-zinc-800 p-6 text-center" data-testid={`gov-cert-${i}`}>
                <cert.icon className="w-10 h-10 text-orange-500 mx-auto mb-4" />
                <h3 className="text-lg uppercase tracking-wide font-bold text-white mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {cert.title}
                </h3>
                <span className="badge bg-orange-500/10 border border-orange-500/50 text-orange-500 px-3 py-1">
                  {cert.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Summary */}
      <section className="py-24 bg-zinc-950 border-t border-zinc-800" data-testid="capabilities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Capabilities Statement
              </h2>
              <p className="text-zinc-300 leading-relaxed mb-8">
                23rd Environmental Units LLC provides professional dry ice blasting and industrial 
                cleaning services to federal, state, and local government agencies. Our mobile 
                service capability allows us to deliver on-site cleaning solutions with minimal 
                disruption to operations.
              </p>
              
              <div className="space-y-4">
                <h3 className="text-xl uppercase tracking-wide font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  Core Competencies
                </h3>
                {[
                  'Dry Ice Blasting (Waterless Industrial Cleaning)',
                  'Fleet & Vehicle Maintenance Support',
                  'Facility & Infrastructure Cleaning',
                  'Fire & Smoke Damage Restoration',
                  'Equipment Decontamination & Restoration',
                  'HVAC System Cleaning'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span className="text-zinc-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-black border border-zinc-800 p-8">
              <h3 className="text-2xl uppercase tracking-wide font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Company Data
              </h3>
              
              <div className="space-y-4">
                {[
                  { label: 'Company Name', value: '23rd Environmental Units LLC' },
                  { label: 'DBA', value: '23rd Industries' },
                  { label: 'UEI', value: 'HWVZSJ4EWJ27' },
                  { label: 'CAGE Code', value: '1AQ57' },
                  { label: 'Location', value: 'Haltom City, TX' },
                  { label: 'Service Area', value: 'DFW Metroplex + 100mi Radius' },
                  { label: 'Business Type', value: 'LLC, Minority-Owned, Woman-Owned' },
                ].map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-3 border-b border-zinc-800">
                    <span className="text-zinc-400 text-sm font-mono uppercase tracking-widest">{item.label}</span>
                    <span className="text-white font-mono">{item.value}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-4 bg-orange-500/10 border border-orange-500/50">
                <p className="text-orange-500 text-sm font-mono">
                  Contact: operations@23rdindustries.com
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-orange-600 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-black mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Request a Capabilities Briefing
          </h2>
          <p className="text-black/80 text-lg mb-8 max-w-2xl mx-auto">
            Let us show you how 23rd Industries can support your government agency's 
            cleaning and maintenance requirements.
          </p>
          <Link to="/contact" className="bg-black text-white px-10 py-4 text-lg uppercase tracking-widest inline-flex items-center gap-2 hover:bg-zinc-900 transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="gov-cta">
            Contact Us
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </main>
  );
};
